// @ts-nocheck
import React, { useState, useEffect, useMemo, useRef } from "react";
import { MarketAsset, fetchMarketHistory, formatCurrencyValue } from "../../services/marketService";
import "./MarketDetailModal.css";

interface Props {
  asset: MarketAsset;
  onClose: () => void;
}

export default function MarketDetailModal({ asset, onClose }: Props) {
  const [range, setRange] = useState<"7d" | "30d" | "90d" | "all">("30d");
  const [historyData, setHistoryData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  // Lock background scroll when modal is open
  useEffect(() => {
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    fetchMarketHistory(asset.symbol, range)
      .then((res) => {
        if (isMounted && res.success) {
          setHistoryData(res.data);
        }
      })
      .catch((err) => {
        console.error("Failed to load history:", err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [asset.symbol, range]);

  // Points for the chart
  const series = useMemo(() => {
    if (historyData && historyData.series && historyData.series.length > 0) {
      return historyData.series;
    }
    // Fallback sparkline if API hasn't loaded yet
    if (asset.sparkline && asset.sparkline.length > 0) {
      return asset.sparkline.map((val, idx) => ({
        date: `Day ${idx + 1}`,
        close: val,
      }));
    }
    return [];
  }, [historyData, asset.sparkline]);

  // Compute SVG coordinates
  const chartMath = useMemo(() => {
    if (series.length < 2) return null;

    const values = series.map((s: any) => s.close);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const rangeVal = max - min === 0 ? 1 : max - min;

    const width = 640;
    const height = 200;
    const paddingX = 10;
    const paddingY = 16;

    const innerW = width - paddingX * 2;
    const innerH = height - paddingY * 2;

    const coords = series.map((item: any, idx: number) => {
      const x = paddingX + (idx / (series.length - 1)) * innerW;
      const y = paddingY + innerH - ((item.close - min) / rangeVal) * innerH;
      return { x, y, ...item };
    });

    // Create SVG smooth bezier path
    let linePath = `M ${coords[0].x} ${coords[0].y}`;
    for (let i = 0; i < coords.length - 1; i++) {
      const curr = coords[i];
      const next = coords[i + 1];
      const mx = (curr.x + next.x) / 2;
      linePath += ` C ${mx} ${curr.y}, ${mx} ${next.y}, ${next.x} ${next.y}`;
    }

    const areaPath = `${linePath} L ${coords[coords.length - 1].x} ${height} L ${coords[0].x} ${height} Z`;

    const isPositive = coords[coords.length - 1].close >= coords[0].close;

    return { width, height, coords, linePath, areaPath, isPositive, min, max };
  }, [series]);

  // Handle chart mouse move for crosshair tooltip
  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || !chartMath || chartMath.coords.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const clientX = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, clientX / rect.width));
    const targetIdx = Math.round(ratio * (chartMath.coords.length - 1));
    setHoverIndex(targetIdx);
  };

  const handleMouseLeave = () => {
    setHoverIndex(null);
  };

  const activePoint = hoverIndex !== null && chartMath ? chartMath.coords[hoverIndex] : null;
  const isUp = (asset.changePercent || 0) >= 0;

  return (
    <div className="market-modal-overlay" onClick={onClose}>
      <div className="market-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="market-modal-header">
          <div className="market-modal-title-wrap">
            <span className="market-modal-flag">{asset.flag || "📈"}</span>
            <div>
              <div className="flex items-center">
                <span className="market-modal-name">{asset.name}</span>
                <span className="market-modal-symbol-badge">{asset.symbol}</span>
              </div>
              <span className="text-[0.8rem] text-[#64748b] capitalize">{asset.category} {asset.unit ? `• ${asset.unit}` : ""}</span>
            </div>
          </div>
          <button className="market-modal-close" onClick={onClose} aria-label="Close modal">
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="market-modal-body">
          {/* Price & Range toggles */}
          <div className="market-modal-price-row">
            <div className="market-modal-price-left">
              <span className="market-modal-big-price">
                {formatCurrencyValue(activePoint ? activePoint.close : asset.price, asset.currency)}
              </span>
              <span className={`market-modal-change-tag ${isUp ? "positive" : "negative"}`}>
                {isUp ? "▲ +" : "▼ "}
                {asset.changePercent ? Math.abs(asset.changePercent).toFixed(2) : "0.00"}% ({isUp ? "+" : ""}{asset.change ? asset.change.toFixed(2) : "0.00"})
              </span>
            </div>

            {/* Timeframe selector */}
            <div className="market-modal-range-toggles">
              {(["7d", "30d", "90d", "all"] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`market-range-btn ${range === r ? "active" : ""}`}
                  onClick={() => setRange(r)}
                >
                  {r === "7d" ? "7D" : r === "30d" ? "1M" : r === "90d" ? "3M" : "ALL"}
                </button>
              ))}
            </div>
          </div>

          {/* Interactive Chart */}
          <div className="market-chart-container">
            {activePoint && (
              <div className="market-chart-tooltip">
                <span>{activePoint.date}: </span>
                <span>{formatCurrencyValue(activePoint.close, asset.currency)}</span>
              </div>
            )}

            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 dark:bg-black/40 z-10 rounded-[14px]">
                <div className="w-6 h-6 border-2 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {chartMath ? (
              <svg
                ref={svgRef}
                className="market-chart-svg"
                viewBox={`0 0 ${chartMath.width} ${chartMath.height}`}
                preserveAspectRatio="none"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              >
                <defs>
                  <linearGradient id={`grad-${asset.symbol}`} x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor={chartMath.isPositive ? "#10b981" : "#ef4444"}
                      stopOpacity="0.28"
                    />
                    <stop
                      offset="100%"
                      stopColor={chartMath.isPositive ? "#10b981" : "#ef4444"}
                      stopOpacity="0.0"
                    />
                  </linearGradient>
                </defs>

                {/* Area Gradient */}
                <path d={chartMath.areaPath} fill={`url(#grad-${asset.symbol})`} />

                {/* Stroke Line */}
                <path
                  d={chartMath.linePath}
                  fill="none"
                  stroke={chartMath.isPositive ? "#10b981" : "#ef4444"}
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />

                {/* Hover Vertical Guide Line & Marker */}
                {activePoint && (
                  <>
                    <line
                      x1={activePoint.x}
                      y1="0"
                      x2={activePoint.x}
                      y2={chartMath.height}
                      stroke="#94a3b8"
                      strokeWidth="1.2"
                      strokeDasharray="3 3"
                    />
                    <circle
                      cx={activePoint.x}
                      cy={activePoint.y}
                      r="5"
                      fill={chartMath.isPositive ? "#10b981" : "#ef4444"}
                      stroke="#ffffff"
                      strokeWidth="2"
                    />
                  </>
                )}
              </svg>
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-[#94a3b8]">
                Chart data unavailable
              </div>
            )}
          </div>

          {/* Auto-generated Deterministic Analysis */}
          <div className="market-modal-analysis-box">
            <i className="fi fi-rr-bulb market-modal-analysis-icon"></i>
            <div className="market-modal-analysis-text">
              {historyData?.analysis ||
                `${asset.name} is currently trading at ${formatCurrencyValue(asset.price, asset.currency)} (${asset.changePercent >= 0 ? "+" : ""}${asset.changePercent}% today).`}
            </div>
          </div>

          {/* Key Statistics Grid */}
          <div className="market-modal-stats-grid">
            <div className="market-stat-card">
              <div className="market-stat-label">Day High</div>
              <div className="market-stat-value">
                {formatCurrencyValue(asset.high || asset.price * 1.01, asset.currency)}
              </div>
            </div>
            <div className="market-stat-card">
              <div className="market-stat-label">Day Low</div>
              <div className="market-stat-value">
                {formatCurrencyValue(asset.low || asset.price * 0.99, asset.currency)}
              </div>
            </div>
            <div className="market-stat-card">
              <div className="market-stat-label">Prev Close</div>
              <div className="market-stat-value">
                {formatCurrencyValue(asset.previousClose || asset.price - asset.change, asset.currency)}
              </div>
            </div>
            <div className="market-stat-card">
              <div className="market-stat-label">Period Return</div>
              <div
                className={`market-stat-value ${
                  (historyData?.periodStats?.periodReturnPercent || 0) >= 0 ? "text-emerald-500" : "text-rose-500"
                }`}
              >
                {(historyData?.periodStats?.periodReturnPercent || 0) >= 0 ? "+" : ""}
                {historyData?.periodStats?.periodReturnPercent ?? asset.changePercent}%
              </div>
            </div>
            <div className="market-stat-card">
              <div className="market-stat-label">Period High</div>
              <div className="market-stat-value">
                {formatCurrencyValue(historyData?.periodStats?.periodHigh || chartMath?.max || asset.price, asset.currency)}
              </div>
            </div>
            <div className="market-stat-card">
              <div className="market-stat-label">Period Low</div>
              <div className="market-stat-value">
                {formatCurrencyValue(historyData?.periodStats?.periodLow || chartMath?.min || asset.price, asset.currency)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
